import LegalLayout from "../components/LegalLayout";

export default function TermsOfUse() {
  return (
    <LegalLayout
      title="Terms of Use"
      lastUpdated="Effective Date: May 2, 2026 · Last Updated: May 2, 2026"
    >
      <h2><span className="doc-section-num">1.</span>Accepting These Terms</h2>
      <p>
        These Terms of Use (&ldquo;Terms&rdquo;) constitute a legally binding agreement between you and
        eGeez Inc.&trade; (&ldquo;eGeez,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;), a Delaware corporation
        with its registered address at 8 The Green, Suite A, Dover, DE 19901, United States.
      </p>
      <p>
        eGeez and the eGeez logo are registered trademarks of eGeez Inc., registered with the
        United States Patent and Trademark Office (USPTO) and in other jurisdictions worldwide.
      </p>
      <p>These Terms govern your access to and use of all eGeez platforms including:</p>
      <ul>
        <li>The eGeez licensed marketplace application</li>
        <li>The eLicensee portal</li>
        <li>The eGeez website at egeez.com</li>
      </ul>
      <p>(collectively the &ldquo;Platform&rdquo; or &ldquo;Services&rdquo;)</p>
      <p>
        <strong>By accessing or using our Services — including simply browsing our website — you
        agree to be bound by these Terms. If you do not agree, you may not use our Services.</strong>
      </p>
      <blockquote>
        <strong>Important Notice:</strong> Section 11 contains an arbitration clause and class
        action waiver. By agreeing to these Terms, you waive your right to a jury trial and your
        right to participate in class action proceedings.
      </blockquote>

      <h2><span className="doc-section-num">2.</span>About eGeez</h2>
      <p>
        eGeez Inc.&trade; operates the world&rsquo;s first centralized licensed consumer products
        marketplace — connecting verified licensees, licensors, and fans globally. eGeez operates
        across three distinct user categories:
      </p>
      <ul>
        <li><strong>Fans</strong> — consumers discovering and purchasing officially licensed products in their authorized territory</li>
        <li><strong>Licensees</strong> — official licensed manufacturers and sellers onboarded through the eLicensee portal</li>
        <li><strong>Licensors</strong> — intellectual property rights holders whose licensed products appear on the Platform</li>
      </ul>
      <p>All users are subject to these Terms regardless of category.</p>

      <h2><span className="doc-section-num">3.</span>Eligibility and Your Account</h2>
      <h3>3.1 Age Requirements</h3>
      <p>
        You must be at least 18 years of age to use our Services independently. Users between 13
        and 17 years of age may only use the Services under the supervision of a parent or legal
        guardian who accepts these Terms on their behalf. Users under 13 years of age are strictly
        prohibited from using our Services.
      </p>
      <h3>3.2 Account Registration</h3>
      <p>
        To access certain features of our Platform you must create an account. By registering you
        agree to:
      </p>
      <ul>
        <li>Provide accurate, current, and complete information</li>
        <li>Maintain and promptly update your account information</li>
        <li>Keep your password secure and confidential</li>
        <li>Accept responsibility for all activity conducted through your account</li>
        <li>Notify us immediately of any unauthorized use of your account at <a href="mailto:support@egeez.com">support@egeez.com</a></li>
      </ul>
      <h3>3.3 Business Accounts</h3>
      <p>
        If you register on behalf of a business entity, you personally represent and warrant that
        you have the legal authority to bind that entity to these Terms.
      </p>
      <h3>3.4 Account Non-Transferability</h3>
      <p>
        Your account is personal to you and may not be transferred, sold, or assigned to any third
        party without our prior written consent.
      </p>
      <h3>3.5 Relationship</h3>
      <p>
        These Terms do not create any agency, partnership, joint venture, employment, or franchise
        relationship between you and eGeez.
      </p>

      <h2><span className="doc-section-num">4.</span>Licensee-Specific Terms</h2>
      <h3>4.1 Eligibility to Sell</h3>
      <p>
        Only official licensees holding valid, current licensing agreements with recognized
        licensors may register as sellers on the eLicensee portal. By registering as a licensee
        you represent and warrant that:
      </p>
      <ul>
        <li>You hold a valid, current licensing agreement with the relevant licensor</li>
        <li>Your products are manufactured and sold in full compliance with your licensing agreement</li>
        <li>You are authorized to sell in the territories you designate on the Platform</li>
        <li>All products listed are genuine licensed products that have passed applicable approval processes</li>
      </ul>
      <h3>4.2 ePIN Verification</h3>
      <p>
        Access to the eLicensee portal requires verification through our proprietary eSmart Gateway
        system. eGeez reserves the right to verify your licensing status through automated and
        human verification processes including domain intelligence analysis, licensee database
        checks, and licensing agreement review. Issuance of an ePIN does not guarantee continued
        access if your licensing status changes.
      </p>
      <h3>4.3 Territory Compliance</h3>
      <p>
        You agree to accurately designate only those territories in which you are licensed to sell.
        eGeez&rsquo;s geofencing technology will enforce territory restrictions based on your uploaded
        licensing agreement. You are solely responsible for ensuring your territory designations
        are accurate and compliant with your licensing agreement at all times.
      </p>
      <h3>4.4 Product Listings</h3>
      <ul>
        <li>Officially licensed and approved by the relevant licensor</li>
        <li>Accurately described with correct pricing, territory availability, and product information</li>
        <li>Compliant with all applicable laws and regulations in designated territories</li>
        <li>Free from any unauthorized alterations or modifications from approved designs</li>
      </ul>
      <h3>4.5 Royalties and Payments</h3>
      <p>
        Licensees acknowledge that eGeez tracks and reports sales data to relevant licensors
        through eGeezPay. You agree to accurate and complete reporting of all sales made through
        the Platform. Attempts to manipulate, misrepresent, or conceal sales data constitute a
        material breach of these Terms and your licensing agreement.
      </p>
      <h3>4.6 Commission</h3>
      <p>
        eGeez charges a commission on sales made through the Platform as detailed in our current
        Fee Schedule available on the eLicensee portal. Commission rates are subject to change
        with 30 days written notice. Early onboarding licensees may be eligible for reduced
        commission rates as specified during their onboarding period.
      </p>

      <h2><span className="doc-section-num">5.</span>Licensor-Specific Terms</h2>
      <h3>5.1 Licensor Participation</h3>
      <p>
        Licensors may participate in the eGeez Platform to monitor their licensed products, access
        sales intelligence, and communicate with their licensees through the Platform. Licensor
        portal access will be made available in a subsequent phase of Platform development.
      </p>
      <h3>5.2 Licensing Agreement Upload</h3>
      <p>
        Licensors who upload licensing agreements to the Platform grant eGeez a limited,
        non-exclusive license to use that information solely for the purpose of verifying licensee
        compliance, enforcing territory restrictions, and generating royalty reports.
      </p>
      <h3>5.3 Gold Verification</h3>
      <p>
        Licensees who upload valid licensing agreements may be eligible for Gold Verification
        status on the Platform. Licensors acknowledge and support this verification process as a
        means of enhancing consumer trust and licensing compliance.
      </p>

      <h2><span className="doc-section-num">6.</span>Your Content</h2>
      <h3>6.1 Ownership</h3>
      <p>
        Content you submit to our Platform including product listings, descriptions, images, and
        business information (&ldquo;Your Content&rdquo;) remains your property. We make no claim of
        ownership over Your Content.
      </p>
      <h3>6.2 License to eGeez</h3>
      <p>
        By submitting Your Content to our Platform, you grant eGeez a worldwide, non-exclusive,
        royalty-free license to use, display, reproduce, and distribute Your Content solely for the
        purposes of operating and improving the Platform.
      </p>
      <h3>6.3 Your Responsibility</h3>
      <ul>
        <li>You own or have all necessary rights to Your Content</li>
        <li>Your Content does not infringe any third party&rsquo;s intellectual property, privacy, or other rights</li>
        <li>Your Content is accurate and not misleading</li>
        <li>Your Content complies with all applicable laws and these Terms</li>
      </ul>
      <h3>6.4 Intellectual Property Violations</h3>
      <p>
        eGeez takes intellectual property rights extremely seriously. If you believe any content
        on our Platform infringes your intellectual property rights, please contact us at{" "}
        <a href="mailto:legal@egeez.com">legal@egeez.com</a>. eGeez will investigate all valid
        complaints and take appropriate action including removal of infringing content and
        termination of offending accounts.
      </p>

      <h2><span className="doc-section-num">7.</span>Prohibited Conduct</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use our Services for any unlawful purpose or in violation of any applicable laws or regulations</li>
        <li>List, sell, or promote counterfeit, unauthorized, or unlicensed products</li>
        <li>Misrepresent your licensing status, territory authorizations, or product information</li>
        <li>Manipulate, underreport, or conceal sales data or royalty obligations</li>
        <li>Attempt to circumvent our geofencing or territory enforcement systems</li>
        <li>Sell products in unauthorized territories</li>
        <li>Reverse engineer, scrape, crawl, or attempt to extract source code from our Platform</li>
        <li>Introduce viruses, malware, or any harmful code to our systems</li>
        <li>Impersonate any person, company, or licensor</li>
        <li>Use our Services to facilitate fraud, deception, or anti-competitive conduct</li>
        <li>Violate any licensor&rsquo;s intellectual property rights or licensing terms</li>
        <li>Use the eGeez trademark or brand assets without our prior written consent</li>
        <li>Engage in shadow production or off-the-books manufacturing of licensed products</li>
      </ul>
      <p>
        Violation of any prohibited conduct may result in immediate account termination, removal
        of all listings, reporting to relevant licensors, and legal action where appropriate.
      </p>

      <h2><span className="doc-section-num">8.</span>Premiere Drop and Pre-Order Terms</h2>
      <h3>8.1 Premiere Drop Feature</h3>
      <p>
        The Premiere Drop feature allows licensees to offer pre-orders on prototype-stage licensed
        products within a licensor-approved window. By participating in Premiere Drop:
      </p>
      <ul>
        <li>Licensees confirm they have obtained explicit licensor approval for the prototype product</li>
        <li>Licensees commit to fulfilling all pre-orders within the stated expected shipping timeframe</li>
        <li>Fans acknowledge that Premiere Drop products are pre-production and subject to minor design variations</li>
        <li>eGeez does not guarantee delivery timelines and is not responsible for delays caused by the licensee or manufacturer</li>
      </ul>
      <h3>8.2 Pre-Order Cancellations</h3>
      <p>
        Fans may cancel pre-orders within 48 hours of placement. After 48 hours cancellation is
        subject to the licensee&rsquo;s stated cancellation policy.
      </p>

      <h2><span className="doc-section-num">9.</span>eShip Program Terms</h2>
      <p>
        Licensees who do not have direct dropshipping capability may apply for the eShip program.
        Participation in eShip is subject to:
      </p>
      <ul>
        <li>Verification that the licensee does not operate an active e-commerce shopping cart on their own website</li>
        <li>Agreement to eShip commission rates and fulfillment terms</li>
        <li>Compliance with all eGeez quality and compliance standards</li>
        <li>eGeez&rsquo;s right to terminate eShip participation at any time with 30 days notice</li>
      </ul>
      <p>
        Misrepresentation of dropshipping capability to gain eShip access constitutes a material
        breach of these Terms.
      </p>

      <h2><span className="doc-section-num">10.</span>Payments, Fees, and Taxes</h2>
      <h3>10.1 Payment Processing</h3>
      <p>
        All payments on the Platform are processed through eGeezPay or our designated payment
        partners. By using our payment services you agree to our Payment Terms available on the
        Platform.
      </p>
      <h3>10.2 Fees</h3>
      <p>
        Current fee schedules including commission rates and subscription fees are available on
        the Platform. eGeez reserves the right to modify fees with 30 days written notice to
        affected users.
      </p>
      <h3>10.3 Taxes</h3>
      <p>
        You are solely responsible for determining and fulfilling your tax obligations arising
        from your use of the Platform including sales tax, VAT, GST, and income tax where
        applicable. eGeez may collect and remit certain taxes where required by law.
      </p>
      <h3>10.4 Disputed Charges</h3>
      <p>
        All fee disputes must be raised within 30 days of the disputed charge. Disputes raised
        after this period will not be considered.
      </p>

      <h2><span className="doc-section-num">11.</span>Disputes</h2>
      <h3>11.1 Governing Law</h3>
      <p>
        These Terms are governed by the laws of the State of Delaware, United States, without
        regard to its conflict of laws provisions. For users outside the United States, mandatory
        consumer protection laws of your jurisdiction may also apply.
      </p>
      <h3>11.2 Arbitration</h3>
      <p>
        You and eGeez agree that any dispute, claim, or controversy arising from or relating to
        these Terms or your use of the Services shall be resolved by final and binding individual
        arbitration administered by the American Arbitration Association (AAA) under its Consumer
        Arbitration Rules then in effect. Arbitration will be conducted in English. Judgment on
        any arbitration award may be entered in any court of competent jurisdiction.
      </p>
      <h3>11.3 Class Action Waiver</h3>
      <p>
        You and eGeez each waive the right to a jury trial and the right to participate in any
        class action, class arbitration, or representative action. All disputes must be brought
        on an individual basis only.
      </p>
      <h3>11.4 Exceptions</h3>
      <p>
        Either party may seek injunctive or other equitable relief in any court of competent
        jurisdiction to prevent irreparable harm pending arbitration. Small claims court actions
        remain available where eligible.
      </p>
      <h3>11.5 EU Users</h3>
      <p>
        EU users may refer disputes to the Centre for Effective Dispute Resolution (CEDR) for
        mediation prior to arbitration.
      </p>
      <h3>11.6 Costs</h3>
      <p>
        For claims not exceeding $10,000 USD, eGeez will bear reasonable arbitration filing and
        administrative fees unless the claim is found to be frivolous or brought for improper
        purpose.
      </p>
      <h3>11.7 Forum</h3>
      <p>
        Any legal proceedings not subject to arbitration shall be brought exclusively in the
        state or federal courts located in Delaware, United States. You consent to the personal
        jurisdiction of such courts.
      </p>

      <h2><span className="doc-section-num">12.</span>Warranties and Limitation of Liability</h2>
      <h3>12.1 Platform Provided As-Is</h3>
      <p>
        Our Services are provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis without warranties
        of any kind, either express or implied, including but not limited to implied warranties
        of merchantability, fitness for a particular purpose, or non-infringement.
      </p>
      <h3>12.2 Products and Sellers</h3>
      <p>
        eGeez operates as a marketplace platform. We verify licensee credentials but do not
        manufacture, store, or inspect products sold through our Platform. Any legal claim related
        to a product must be brought directly against the relevant licensee/seller.
      </p>
      <h3>12.3 Limitation of Liability</h3>
      <p>
        To the maximum extent permitted by applicable law, eGeez&rsquo;s total liability to you for
        any claims arising from these Terms or your use of the Services shall not exceed the
        greater of (a) the total fees paid by you to eGeez in the 12 months preceding the claim,
        or (b) $100 USD.
      </p>
      <p>
        eGeez shall not be liable for any indirect, incidental, special, consequential, or
        punitive damages including lost profits, lost data, or loss of goodwill.
      </p>
      <h3>12.4 Indemnification</h3>
      <p>
        You agree to defend, indemnify, and hold harmless eGeez and its officers, directors,
        employees, and agents from any claims, damages, losses, liabilities, and expenses
        (including reasonable legal fees) arising from your use of the Services, your Content,
        your violation of these Terms, or your violation of any third party&rsquo;s rights.
      </p>

      <h2><span className="doc-section-num">13.</span>Termination</h2>
      <h3>13.1 By You</h3>
      <p>
        You may terminate your account at any time through your account settings. Termination
        does not relieve you of any outstanding payment obligations.
      </p>
      <h3>13.2 By eGeez</h3>
      <p>
        eGeez may suspend or terminate your account and access to Services at any time if we
        determine you have violated these Terms, your licensing agreement obligations, or
        applicable law. We will generally provide notice of termination unless prohibited by law
        or where violations are repeated or severe.
      </p>
      <h3>13.3 Effect of Termination</h3>
      <p>
        Upon termination all licenses granted to you under these Terms immediately cease. Sections
        that by their nature should survive termination will survive including Sections 6, 9, 11,
        12, and 13.
      </p>

      <h2><span className="doc-section-num">14.</span>Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. For material changes we will provide at
        least 30 days advance notice via email or prominent Platform notice. Your continued use
        of our Services following the effective date of changes constitutes your acceptance of the
        updated Terms. If you do not agree to updated Terms you must stop using our Services and
        may close your account.
      </p>

      <h2><span className="doc-section-num">15.</span>General Provisions</h2>
      <h3>15.1 Entire Agreement</h3>
      <p>
        These Terms together with our Privacy Policy constitute the entire agreement between you
        and eGeez regarding the Services and supersede all prior agreements.
      </p>
      <h3>15.2 Severability</h3>
      <p>
        If any provision of these Terms is found unenforceable, that provision will be modified to
        the minimum extent necessary and the remaining provisions will continue in full force and
        effect.
      </p>
      <h3>15.3 No Waiver</h3>
      <p>
        Our failure to enforce any provision of these Terms shall not constitute a waiver of our
        right to enforce it in the future.
      </p>
      <h3>15.4 Assignment</h3>
      <p>
        eGeez may assign its rights and obligations under these Terms without your consent. You
        may not assign your rights under these Terms without our prior written consent.
      </p>
      <h3>15.5 Force Majeure</h3>
      <p>
        eGeez shall not be liable for any failure or delay in performance resulting from causes
        beyond our reasonable control including acts of God, war, terrorism, pandemic, government
        action, or internet infrastructure failure.
      </p>

      <h2><span className="doc-section-num">16.</span>Contact Us</h2>
      <p>
        For any questions, concerns, or legal notices regarding these Terms please contact us:
      </p>
      <p>
        <strong>eGeez Inc.&trade;</strong><br />
        8 The Green, Suite A<br />
        Dover, DE 19901<br />
        United States<br />
        Email: <a href="mailto:support@egeez.com">support@egeez.com</a><br />
        Legal: <a href="mailto:legal@egeez.com">legal@egeez.com</a><br />
        Website: <a href="https://www.egeez.com" target="_blank" rel="noopener noreferrer">www.egeez.com</a>
      </p>

      <hr />
      <p className="text-black/55">
        <em>eGeez Inc.&trade; — All rights reserved. eGeez is a registered trademark of eGeez Inc., registered with the USPTO and in other jurisdictions worldwide.</em>
      </p>
    </LegalLayout>
  );
}
